import { all, call, fork, put, takeLatest } from "redux-saga/effects";
import { getJWT } from "../../services/restAPI/RestApi";
import { getUserData, getUserWithBoards } from "../../services/restAPI/endpoints/User/User";
import { getRegistry } from "../../services/restAPI/endpoints/Registry";
import { getVisibiltyOptions } from "../../services/restAPI/endpoints/OrganizationsApi";
import { push } from "connected-react-router";
import { getAllLanguages } from "../../services/restAPI/endpoints/Translations";
import { setAllLanguages } from "../actions/Setting";
import { setUserInfo } from "../actions/User";
import { setUserBoards } from "../actions/Boards";
import {
   hideAuthLoading,
   setLoggingIn,
   setOrganization,
   showAuthLoading,
   showAuthMessage,
   userSignInFailure,
   userSignInSuccess,
   userSignOutSuccess,
} from "../actions/Auth";
import { isHoppyOrGamification } from "../../util/Scripts";
import { setVisibilityOptions } from "../actions/VisibilityOptions";

// helper to centralize local/sessionStorage cleanup
function clearOldSession() {
   ["publicID", "isPublicUserLogged"].forEach((k) => localStorage.removeItem(k));
   ["autoLoginToken", "autoLoginLocation", "autoLoginProcess"].forEach((k) =>
      sessionStorage.removeItem(k),
   );
}

// helper to decide where to redirect
function* handleRedirect(user) {
   const loc = sessionStorage.getItem("autoLoginLocation");
   const czg = sessionStorage.getItem("redirectCzgLink");
   let target = user.mainPageUrl || "/cases/new";

   if (loc) {
      target = loc;
      sessionStorage.removeItem("autoLoginLocation");
   } else if (czg) {
      target = czg;
      sessionStorage.removeItem("redirectCzgLink");
   }

   // normalize
   if (target === "/messages/new") target = "/cases/new";

   // if first-time gamification
   if (user.isLoggedFirstTime && isHoppyOrGamification()) {
      window.location.replace(user.firstLoginRedirectUrl);
      return;
   }

   // push via router
   yield put(push(target));
}

function* fetchLanguages() {
   const { data } = yield call(getAllLanguages);
   if (data) {
      yield put(setAllLanguages(data));
   }
}

function* fetchJWT() {
   try {
      const { data: jwt } = yield call(getJWT);
      localStorage.setItem("pocJWT", jwt);
   } catch (err) {
      if (err.response?.status === 402) {
         yield put(push("/renew-subscription"));
         // throw to break out of main flow
         throw new Error("SUBSCRIPTION_EXPIRED");
      }
      // otherwise ignore
   }
}

function* fetchUserData() {
   const { data: user } = yield call(getUserData);
   // clear old
   clearOldSession();

   // store id
   localStorage.setItem("userID", user.id);

   // normalize
   user.mainPageUrl = user.mainPageUrl === "/messages/new" ? "/cases/new" : user.mainPageUrl;

   // lightweight boards early
   if (!sessionStorage.getItem("boardDetailsResponseAppeared")) {
      const { boards, ...lightUser } = user;
      yield put(setUserInfo(lightUser));
      yield put(setUserBoards(boards));
   }

   // pick first company
   const firstCompany = user.companies?.[0] || {};
   yield put(setOrganization({ id: firstCompany.oldId, name: firstCompany.name }));

   return user;
}

function* backgroundVisibilityLoad() {
   try {
      const { data } = yield call(getVisibiltyOptions);
      yield put(setVisibilityOptions(data));
   } catch (e) {
      console.error(e);
   }
}

function* handleHeadlessSignIn() {
   try {
      // 1. Prep
      yield fork(fetchLanguages);
      yield put(setLoggingIn(true));
      sessionStorage.removeItem("boardDetailsResponseAppeared");

      // 2. JWT
      yield call(fetchJWT);

      // 3. User data + all sub-steps
      const user = yield call(fetchUserData);

      // 4. complete login
      yield put(userSignInSuccess(user));

      // 5. kick off background
      yield fork(backgroundVisibilityLoad);

      return user;
   } catch (err) {
      console.error("Headless sign-in failed:", err);

      throw err;
   }
}

function* handleSignIn() {
   try {
      yield put(showAuthLoading());

      const user = yield call(handleHeadlessSignIn);

      yield call(handleRedirect, user);
   } catch (err) {
      // on any error (except SUBSCRIPTION_EXPIRED) re-enable button
      if (err.message !== "SUBSCRIPTION_EXPIRED") {
         sessionStorage.removeItem("autoLoginProcess");
         sessionStorage.removeItem("autoLoginLocation");
         // you might put an action that resets `loggingIn` or re-enables the button
         yield put(hideAuthLoading());
         yield put(setLoggingIn(false));
         yield put(userSignInFailure());
         console.log("Sign-in failed:", err);
      }
   }
}

function clearAllAuthData() {
   localStorage.removeItem("pocJWT");
   localStorage.removeItem("userID");
   sessionStorage.removeItem("boardDetailsResponseAppeared");
   sessionStorage.removeItem("autoLoginToken");
   sessionStorage.removeItem("autoLoginLocation");
   sessionStorage.removeItem("autoLoginProcess");
   sessionStorage.removeItem("redirectCzgLink");
}

function* handleSignOut() {
   try {
      yield call(clearAllAuthData);

      yield put(userSignOutSuccess());
   } catch (error) {
      yield put(showAuthMessage(error));
   }
}

export function* watchSignOut() {
   yield takeLatest("SIGNOUT_USER", handleSignOut);
}

export function* watchSignIn() {
   yield takeLatest("SIGNIN_USER", handleSignIn);
}

export function* watchHeadlessSignIn() {
   yield takeLatest("HEADLESS_SIGNIN_USER", handleHeadlessSignIn);
}

export default function* authRootSaga() {
   yield all([fork(watchSignIn), fork(watchSignOut), fork(watchHeadlessSignIn)]);
}
